import {
  DataResidency,
  HomeCountry,
  residencyForHomeCountry,
} from './regionAndResidency';

/**
 * Where a resolved `home_country` came from, highest-precedence first.
 * `explicit` is a prior user choice ("chosen"); the others are inferred
 * ("detected"); `default` is the no-signal fallback.
 */
export type HomeCountrySource = 'explicit' | 'geolocation' | 'ip' | 'default';

/**
 * Pre-login `home_country` signals, gathered by the app layer. Precedence runs
 * highest to lowest: explicit prior user choice, browser geolocation, IP-based
 * backend guess. Each value is an ISO 3166-1 alpha-3 country (case-insensitive).
 *
 * The browser I/O that produces these signals (localStorage, the Geolocation
 * API, the backend fetch) deliberately lives in the app, not here, so this
 * module stays isomorphic and pure.
 */
export interface HomeCountrySignals {
  /** A prior explicit user selection, persisted client-side. */
  explicitChoice?: HomeCountry;
  /** Country from browser geolocation, reverse-geocoded by the backend. */
  geolocationCountry?: HomeCountry;
  /** Country from the IP-based backend guess. */
  ipCountry?: HomeCountry;
}

export interface HomeCountryResolution {
  homeCountry: HomeCountry;
  dataResidency: DataResidency;
  source: HomeCountrySource;
}

const normalize = (value: HomeCountry | undefined): HomeCountry | undefined =>
  value?.trim().toUpperCase() || undefined;

/**
 * Resolve the pre-login `home_country` from the available signals by
 * precedence, and derive its data residency. Falls back to the `UNK` sentinel
 * (which derives to the default residency) when no signal is present.
 */
export function resolveHomeCountry(
  signals: HomeCountrySignals,
): HomeCountryResolution {
  const explicit = normalize(signals.explicitChoice);
  const geolocation = normalize(signals.geolocationCountry);
  const ip = normalize(signals.ipCountry);

  let homeCountry: HomeCountry;
  let source: HomeCountrySource;
  if (explicit) {
    homeCountry = explicit;
    source = 'explicit';
  } else if (geolocation) {
    homeCountry = geolocation;
    source = 'geolocation';
  } else if (ip) {
    homeCountry = ip;
    source = 'ip';
  } else {
    homeCountry = 'UNK';
    source = 'default';
  }

  return {
    homeCountry,
    dataResidency: residencyForHomeCountry(homeCountry),
    source,
  };
}
